<template>
  <Toast />
  <div class="signup-container">
    <div class="sidebar-container d-none d-lg-block">
      <AuthSidebar />
      <Sidebar v-model:visible="visibleLeft" :baseZIndex="10000"> </Sidebar>
    </div>

    <div class="content-container">
      <div class="auth-card">
        <div class="logo-container">
          <img
            loading="lazy"
            src="/img/logo.svg"
            alt="CrowdHrs"
            title="CrowdHrs"
            class="form-logo"
          />
        </div>
        
        <div v-if="feedback" class="notification error">
          <span class="notification-text">{{ feedbackMassage }}</span>
          <button class="notification-close" @click="feedback = false">×</button>
        </div>

        <form @submit.prevent="handleLogin(!v$.$invalid)" class="form-fields">
          <!-- Name fields -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="firstname"
                  v-model="v$.firstname.$model"
                  :class="{ 'input-error': v$.firstname.$invalid && submitted }"
                  class="w-100"
                />
                <label for="firstname">{{ $t('firstname') }}</label>
              </span>
              <small v-if="v$.firstname.$invalid && submitted" class="error-text">
                {{ $t('firstname') }} is required
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="lastname"
                  v-model="v$.lastname.$model"
                  :class="{ 'input-error': v$.lastname.$invalid && submitted }"
                  class="w-100"
                />
                <label for="lastname">{{ $t('lastname') }}</label>
              </span>
              <small v-if="v$.lastname.$invalid && submitted" class="error-text">
                {{ $t('lastname') }} is required
              </small>
            </div>
          </div>

          <!-- Email and Password -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="workemail"
                  type="email"
                  v-model="v$.workemail.$model"
                  :class="{ 'input-error': v$.workemail.$invalid && submitted }"
                  class="w-100"
                />
                <label for="workemail">{{ $t('workemail') }}</label>
              </span>
              <small v-if="v$.workemail.$invalid && submitted" class="error-text">
                Please enter a valid work email
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <Password
                  id="password"
                  class="w-100"
                  v-model="v$.password.$model"
                  inputClass="w-100"
                  :class="{ 'input-error': v$.password.$invalid && submitted }"
                  toggleMask
                >
                  <template #header>
                    <h6>{{ $t('pickpassowrd') }}</h6>
                  </template>
                  <template #footer="sp">
                    {{ sp.level }}
                    <Divider />
                    <p class="mt-2">{{ $t('sugges') }}</p>
                    <ul class="pl-2 ml-2 mt-0" style="line-height: 1.5">
                      <li>{{ $t('onelower') }}</li>
                      <li>{{ $t('oneuppercase') }}</li>
                      <li>{{ $t('onenumeric') }}</li>
                      <li>{{ $t('mineightchar') }}</li>
                    </ul>
                  </template>
                </Password>
                <label for="password">{{ $t('password') }}</label>
              </span>
              <small v-if="v$.password.$invalid && submitted" class="error-text">
                Password is required
              </small>
            </div>
          </div>

          <!-- Phone and Company name -->
          <div class="form-row">
            <div class="form-group">
              <PhoneNumberInput 
                v-model="phone" 
                :class="{ 'has-error': v$.phone.$invalid && submitted }"
                :disabled="loading" 
                @is-valid="phoneIsValid"
              />
              <small v-if="v$.phone.$invalid && submitted" class="error-text">
                A valid phone number is required
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="companyname"
                  v-model="v$.companyname.$model"
                  :class="{ 'input-error': v$.companyname.$invalid && submitted }"
                  class="w-100"
                  @change="test"
                />
                <label for="companyname">{{ $t('companyname') }}</label>
              </span>
              <small v-if="v$.companyname.$invalid && submitted" class="error-text">
                Company name is required
              </small>
            </div>
          </div>

          <!-- Country and Industry -->
          <div class="form-row">
            <div class="form-group">
              <CountryCitySelector 
                v-model="locationData"
                :class="{ 'input-error': v$.locationData.country.$invalid && submitted }"
                :showCountry="true"
                :showCity="false"
              />
              <small v-if="v$.locationData.country.$invalid && submitted" class="error-text">
                Country is required
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <Dropdown
                  v-model="v$.selectedIndustry.$model"
                  :options="industries"
                  filter
                  optionLabel="description"
                  :class="{ 'input-error': v$.selectedIndustry.$invalid && submitted }"
                  class="w-100"
                />
                <label for="industry">Industry</label>
              </span>
              <small v-if="v$.selectedIndustry.$invalid && submitted" class="error-text">
                Industry is required
              </small>
            </div>
          </div>

          <!-- Website and Reference -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="companyweb"
                  v-model="v$.companyweb.$model"
                  :class="{ 'input-error': v$.companyweb.$invalid && submitted }"
                  class="w-100"
                />
                <label for="companyweb">{{ $t('companyweb') }}</label>
              </span>
              <small v-if="v$.companyweb.$invalid && submitted" class="error-text">
                Company website is required
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <Dropdown
                  id="referance"
                  v-model="v$.referance.$model"
                  :options="companies"
                  optionLabel="description"
                  optionValue="id"
                  :class="{ 'input-error': v$.referance.$invalid && submitted }"
                  class="w-100"
                />
                <label for="referance">{{ $t('referance') }}</label>
              </span>
              <small v-if="v$.referance.$invalid && submitted" class="error-text">
                Reference is required
              </small>
            </div>
          </div>

          <!-- Terms and conditions -->
          <div class="terms-container">
            <div class="checkbox-wrapper">
              <Checkbox
                inputId="accept"
                name="accept"
                value="Accept"
                v-model="v$.accept.$model"
                :class="{ 'input-error': v$.accept.$invalid && submitted }"
              />
              <label for="accept" class="m-0" :class="{ 'text-error': v$.accept.$invalid && submitted }">
                {{ $t('agreement') }}
              </label>
            </div>
            <router-link :to="{ name: 'termsOfService' }" class="terms-link">
              {{ $t('terms') }}
            </router-link>
          </div>

          <!-- Submit button -->
          <div class="form-actions">
            <CButton
              type="submit"
              :label="$t('signupm')"
              class="w-100"
              variant="solid"
              color="secondary"
              :loading="loading"
            />
          </div>

          <!-- Social login section (only if social is true) -->
          <div v-if="socail" class="social-login">
            <div class="social-divider">
              <span class="divider-line"></span>
              <span class="divider-text">{{ $t('loginwithsocial') }}</span>
              <span class="divider-line"></span>
            </div>

            <div class="social-buttons">
              <div class="google-login-container">
                <GoogleLogin :callback="loginWithGoogle" />
              </div>
              
              <Button
                class="social-button linkedin-button"
                @click.prevent="loginWithGoogle"
              >
                <img
                  loading="lazy"
                  src="/img/Linkden.png"
                  width="20"
                  height="20"
                  alt="LinkedIn"
                />
                <span>{{ $t('linkden') }}</span>
              </Button>
            </div>
          </div>
        </form>

        <!-- Login link -->
        <div class="create-account">
          <span>{{ $t('hasaccount') }}</span>
          <router-link :to="{ name: 'login' }" class="login-link">
            {{ $t('loginm') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, email } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import AuthSidebar from '../components/AuthSidebar.vue'
import { decodeCredential, googleOneTap, GoogleLogin } from 'vue3-google-login'
import { getItem, setItem } from '../store/localStorage'
import { post, get } from '../api/Services'
import { mapState } from 'vuex'
import PhoneNumberInput from '@/components/PhoneNumberInput.vue'
import CountryCitySelector from '@/components/CountryCitySelector.vue'

export default {
  name: 'Signup',
  setup: () => ({ v$: useVuelidate() }),
  components: {
    AuthSidebar,
    GoogleLogin,
    PhoneNumberInput,
    CountryCitySelector,
  },

  validations() {
    return {
      firstname: {
        required,
      },
      locationData: {
        country: {
          required: (value) => !!value,
        }
      },
      lastname: {
        required,
      },
      workemail: {
        required,
        email,
      },
      password: {
        required,
      },

      accept: {
        required,
      },
      companyname: {
        required,
      },
      phone: {
        required,
        valid: (value) => this.isPhoneValid
      },
      referance: {
        required,
      },
      companyweb: {
        required,
      },
      selectedIndustry: {
        required,
      },
      visibleLeft: true,
    }
  },
  data() {
    return {
      errorBorderColor: '#B00020 !important',
      colorRed: { color: '#B00020' },
      loading: false,
      industries: [],
      countries: [],
      selectedCountries: null,
      selectedIndustry: null,
      message: '',
      display: false,
      firstname: '',
      lastname: '',
      workemail: '',
      referance: '',
      companyname: '',
      password: '',
      submitted: false,
      identifyCodes: '234567890abcdefgxyzkmnAp',
      identifyCode: '',
      accept: '',
      securityCode: '',
      companies: [],
      user: {},
      marketing: {},
      roleId: getItem('roleId'),
      socail: false,
      display1: false,
      phone: '',
      isPhoneValid: false,
      companyweb: '',
      feedback: false,
      feedbackMassage: '',
      locationData: {
        country: null,
        city: null
      },
    }
  },

  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn
    },
    ...mapState({
      account: (state) => state.account,
    }),
  },

  created() {
    this.getCountries()
    this.identifyCode = ''
    this.makeCode(this.identifyCodes, 4)
    //   let userId=this.account.loggeduser.id
    // this.marketing.userId = -1;
    this.marketing.sqlId = 22
    let parameters = [
      /*{ parameter_name: "pin_user_id", parameter_value: -1 }*/
    ]
    this.marketing.parameters = JSON.stringify(parameters)

    post('Commons/Executer', this.marketing).then((data) => {
      if (data) {
        this.companies = data
      }
    })
    this.getindustries()
  },

  watch: {
    locationData: {
      handler(newVal) {
        if (newVal && newVal.country) {
          this.selectedCountries = newVal.country;
        }
      },
      deep: true
    }
  },

  methods: {
    async getCountries() {
      this.loading = true
      let parameters = [
        {
          parameter_name: 'pin_user_id',
          parameter_value: this.account.loggeduser
            ? this.account.loggeduser.id
            : -1,
        },
      ]

      await post('/Commons/Executer', { sqlId: 71 }, JSON.stringify(parameters))
        .then((data) => {
          this.countries = data
        })
        .catch((err) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.message,
            life: 3000,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async getindustries() {
      await get('/Industries').then((data) => {
        if (data) {
          this.industries = data
        }
      })
    },
    randomString(length) {
      let result = ''
      const characters =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      const charactersLength = characters.length
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        )
      }
      return result
    },
    test() {},
    loginWithGoogle() {
      googleOneTap()
        .then((response) => {
          // This promise is resolved when user selects an account from the the One Tap prompt
          const googleData = decodeCredential(response.credential)

          this.firstname = googleData.family_name
          this.lastname = googleData.given_name
          // this.user.linkedin = this.linkdenPage;
          this.email = googleData.email
          this.password = googleData.sub
          this.company = { name: 'Google' }
          this.submitted = true
        })
        .catch((error) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error,
          })
        })
    },

    logIn() {
      this.$router.push({ name: 'login' })
    },
    generateSeoUrl(text) {
      return text
        .toString() // Ensure the input is a string
        .normalize('NFD') // Normalize to decompose combined letters (e.g., é -> e + ´)
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (accents, etc.)
        .toLowerCase() // Convert to lower case
        .trim() // Remove leading and trailing whitespace
        .replace(/[^a-z0-9\s-]/g, '') // Remove all non-alphanumeric characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    },
    isRequired(value) {
      if (value && value.trim()) {
        return true
      }

      return 'This is required'
    },
    goDashboard() {
      window.location = '/'
    },

    handleLogin(isFormValid) {
      this.feedback = false
      this.message = ''
      this.submitted = true
      
      // If phone is empty or not valid, mark validation as invalid
      if (!this.phone || !this.isPhoneValid) {
        this.v$.phone.$touch();
      }
      
      if (!isFormValid) {
        this.message = 'No Valid'
        return
      }

      this.loading = true
      this.user.firstName = this.firstname
      this.user.lastName = this.lastname
      this.user.email = this.workemail
      this.user.password = this.password
      this.user.phone = this.phone
      this.user.companyName = this.companyname
      this.user.companyWebsite = this.companyweb
      this.user.marketingChannelId = this.referance
      this.user.industryId = this.selectedIndustry.id
      
      // Use locationData.country instead of selectedCountries
      const countryData = this.locationData.country || this.selectedCountries;
      this.user.countryId = countryData.id
      this.user.country = countryData.iso2
      this.user.currency = countryData.default_curreny
      
      // Add city data if available
      if (this.locationData.city) {
        this.user.cityId = this.locationData.city.id
      }

      post('Users/Company-SignUp', this.user).then(async (data) => {
        // item3 içindeki mesajı ve status bilgisini işleme
        let parsedItem3
        try {
          parsedItem3 = JSON.parse(data.result.item3)
        } catch (error) {
          console.error('item3 JSON parse error:', error)
          parsedItem3 = [
            { status: 'Unknown', message: 'Invalid item3 format.' },
          ]
        }
        const item3Status = parsedItem3[0]?.status || 'Unknown'
        const item3Message = parsedItem3[0]?.message || 'Unknown error.'

        // item3'ün status değerine göre kontrol
        if (item3Status == '0') {
          // Rastgele string oluşturup sessionStorage'a kaydetme
          this.account.emailConfirm = this.randomString(10)
          sessionStorage.setItem('emailConfirm', this.account.emailConfirm)

          // Verileri localStorage'a kaydetme
          await this.CreateCustomers(
            data.result.item1,
            parsedItem3[0]?.company_id
          )

          // Geri bildirim ayarları
          this.feedback = true
          this.feedbackMassage = `${item3Message}`

          // Yönlendirme
          this.$router.push(
            `/login?status=${
              this.account.emailConfirm
            }&message=${this.generateSeoUrl(item3Message)}&severity=success`
          )
        } else {
          // Hata durumunda geri bildirim ayarları
          this.feedback = true
          this.feedbackMassage = `${item3Message}`
          this.loading = false
        }
      })
    },
    async CreateCustomers(customerID, companyID) {
      try {
        const response = await post(
          `/Payments/CreateCustomersCompanyId?customerId=${customerID}&companyId=${companyID}`
        )
        console.log(response)

        if (response) {
          await this.updateStripeCustomer(customerID, companyID)
        }
      } catch (error) {
        console.error('API error:', error)
      }
    },
    /*async CreateCustomers(customerID, secondCustomerID, companyID) {
      try {
        const response = await post(
          `/Payments/CreateCustomersCompanyId?firstCustomerId=${customerID}&secondCustomerId=${secondCustomerID}&companyId=${companyID}`
        )
        console.log(response)

        if (response) {
          await this.updateStripeCustomer(customerID, companyID)
        }
      } catch (error) {
        console.error('API error:', error)
      }
    },*/

    async updateStripeCustomer(customerID, companyID) {
      const parameters = [
        {
          parameter_name: 'pin_user_id',
          parameter_value: -1,
        },
        { parameter_name: 'pin_company_id', parameter_value: companyID },
        {
          parameter_name: 'piv_stripe_customer_id',
          parameter_value: customerID,
        },
      ]

      return await post('Commons/Executer', {
        sqlId: 81,
        parameters: JSON.stringify(parameters),
      })
    },

    randomNum(min, max) {
      return Math.floor(Math.random() * (max - min) + min)
    },

    makeCode(o, l) {
      for (let i = 0; i < l; i++) {
        this.identifyCode +=
          this.identifyCodes[this.randomNum(0, this.identifyCodes.length)]
      }
    },

    phoneIsValid(isValid) {
      this.isPhoneValid = isValid;
    },
  },
}
</script>

<style lang="scss" scoped>
.signup-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: #f7f9fc;
}

.sidebar-container {
  flex: 0 0 33.333%;
  height: 100vh;
}

.content-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.auth-card {
  width: 100%;
  max-width: 550px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  margin: 0 auto;
  transition: all 0.2s ease;
}

.logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.form-logo {
  max-width: 180px;
  height: auto;
}

.notification {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  
  &.error {
    background-color: rgba(220, 53, 69, 0.1);
    border-left: 3px solid #dc3545;
    color: #dc3545;
  }
  
  .notification-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: inherit;
  }
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: flex;
  gap: 1.5rem;
  
  .form-group {
    flex: 1;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  margin-bottom: 0 !important;
  
  ::v-deep(.p-float-label) {
    display: block;
    width: 100%;
    margin-bottom: 0.25rem;
    
    label {
      color: #6c757d;
    }
    
    input:focus ~ label,
    .p-inputwrapper-focus ~ label {
      color: #015cac;
    }
  }
  
  ::v-deep(:not(.p-dropdown) .p-inputtext),
  ::v-deep(.p-password input) {
    width: 100% !important;
    padding: 0.75rem;
    font-size: 1rem;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 6px;
    transition: all 0.25s;
    box-sizing: border-box;
    
    &:hover {
      border-color: #015cac;
    }
    
    &:focus {
      border-color: #015cac;
      box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
    }
    
    &.input-error {
      border-color: #dc3545;
      
      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
      }
    }
  }

  ::v-deep(.p-dropdown) {
    width: 100% !important;
    border: 1px solid #ced4da;
    border-radius: 6px;
    background-color: #fff;
    transition: all 0.25s;
    
    &:hover {
      border-color: #015cac;
    }
    
    &:focus, &.p-focus {
      border-color: #015cac;
      box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
    }
    
    &.input-error {
      border-color: #dc3545;
    }
    
    .p-dropdown-label {
      padding: 0.75rem;
      display: flex;
      align-items: center;
    }
  }
  
  .error-text {
    color: #dc3545;
    margin-top: 0.25rem;
    font-size: 0.8rem;
  }
}

.phone-input {
  ::v-deep(.phone-input-container) {
    width: 100%;
    
    .PhoneInputInput {
      width: 100%;
      padding: 0.75rem;
      font-size: 1rem;
      background-color: #fff;
      border: 1px solid #ced4da;
      border-radius: 6px;
      height: auto;
      
      &:hover {
        border-color: #015cac;
      }
      
      &:focus {
        border-color: #015cac;
        box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
      }
    }
  }
}

.terms-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0.5rem 0;
  
  .checkbox-wrapper {
    display: flex;
    align-items: center;
    
    ::v-deep(.p-checkbox) {
      margin-right: 0.5rem;
    }
  }
  
  label {
    color: #6c757d;
    
    &.text-error {
      color: #dc3545;
    }
  }
  
  .terms-link {
    color: #015cac;
    text-decoration: none;
    margin-left: 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.form-actions {
  margin-top: 0.5rem;
  
  ::v-deep(.chrs-btn-secondary) {
    height: 48px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.25s ease;
  }
}

.social-login {
  margin-top: 2rem;
  
  .social-divider {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    
    .divider-line {
      flex-grow: 1;
      height: 1px;
      background-color: #dee2e6;
    }
    
    .divider-text {
      padding: 0 1rem;
      color: #6c757d;
      font-size: 0.9rem;
    }
  }
  
  .social-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    .google-login-container {
      width: 100%;
      
      ::v-deep(button) {
        width: 100%;
        height: 48px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #dee2e6;
        background-color: #fff;
        transition: all 0.25s;
        
        &:hover {
          background-color: rgba(219, 68, 55, 0.1);
          border-color: #db4437;
        }
      }
    }
    
    .social-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      height: 48px;
      border-radius: 6px;
      font-weight: 500;
      transition: all 0.25s ease;
      border: 1px solid #dee2e6;
      background-color: #fff;
      color: #333;
      
      &:hover {
        background-color: rgba(0, 119, 181, 0.1);
        border-color: #0077b5;
      }
      
      span {
        flex-grow: 1;
        text-align: center;
      }
    }
  }
}

.create-account {
  margin-top: 2rem;
  text-align: center;
  
  span {
    color: #6c757d;
    font-size: 0.95rem;
  }
  
  .login-link {
    margin-left: 0.5rem;
    color: #015cac;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 991.98px) {
  .signup-container {
    flex-direction: column;
  }
  
  .content-container {
    min-height: 100vh;
    padding: 2rem 1rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 1.5rem;
  }
}
</style>
